import { MenuItem, Select, Tooltip, Typography } from '@material-ui/core'
import { addUnc } from '@root-gipro/modules/userProjects/store/actions'
import { IState } from '@root-gipro/store/interfaces'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import './UncAssist.scss'

// Импортируем необходимые интерфейсы
import { IUncAddBuffer } from '@root-gipro/modules/userProjects/models/interfaces/user-projects.model'

// Константы
const CONSTANTS = {
	TOOLTIP: {
		ENTER_DELAY: 500,
		NEXT_DELAY: 100,
		PLACEMENT: 'top' as const,
	},
	MESSAGES: {
		NO_DATA: 'Нет данных',
		NO_DUPLICATES: 'Нет дублирования расценок',
		NO_REQUIRED: 'Нет рекомендуемых к обязательному добавлению расценок',
		NO_RECOMMENDED: 'Нет рекомендуемых к добавлению расценок',
		SELECT_UNC: 'Выберите расценку',
	},
	MAX_AUTO_ADD_ITEMS: 2, // Максимальное количество элементов для автодобавления
	ADD_DELAY: 100, // Задержка между добавлениями расценок
} as const

// Утилитарные функции
const arrayUtils = {
	// Функция для сравнения двух массивов
	isEqual: (arr1: number[], arr2: number[]): boolean => {
		if (arr1.length !== arr2.length) return false
		return arr1.every((val, idx) => val === arr2[idx])
	},

	// Функция для проверки, содержится ли уже массив в массиве массивов
	isArrayInArrayOfArrays: (array: number[], arrayOfArrays: number[][]): boolean => {
		return arrayOfArrays.some(arr => arrayUtils.isEqual(arr, array))
	},
}

const stringUtils = {
	// Проверка равенства строковых ID
	isIdEqual: (id1: number | string, id2: number | string): boolean => {
		return String(id1) === String(id2)
	},
}

// Расширяем интерфейс для группированных расценок
interface IGroupedRecommendation {
	sourceUncId: number
	sourceName: string
	recommendations: number[][]
}

// Интерфейс для групп обязательных расценок для старой функции группировки
interface IRequiredGroupOld {
	parentUnc: any // IUncCell
	requiredIds: number[]
}

// Интерфейс для групп обязательных расценок с новым подходом
interface IRequiredGroup {
	source: IUnc
	requiredIds: number[]
}

// Интерфейсы
interface IUnc {
	id: number | string
	code: string
	name?: string
	parameter?: string
	uncMainId?: number
}

interface IUncCellAssist {
	requiredUncCellIds: number[]
	duplicateUncCellIds: string
	recommendUncCellIds: number[][]
	sourceUncId?: number
}

// Добавляем новый интерфейс для группировки обязательных расценок
interface IRequiredGroup {
	parentUnc: IUnc
	requiredIds: number[]
}

// Интерфейс для накопленных рекомендаций
interface IAccumulatedRecommendations {
	requiredUncCellIds: number[]
	duplicateUncCellIds: string
	recommendUncCellIds: number[][]
	sourceMap: Map<number | string, number[][]>
}

// Добавляем интерфейс для отслеживания циклических зависимостей
interface IDependencyGraph {
	[uncId: number]: number[]
}

interface Props {
	uncCellAssist: IUncCellAssist
	uncAddBuffer: IUncAddBuffer[]
}

// Кеш для хранения данных по разным расценкам
interface UncAssistCache {
	[cacheKey: string]: {
		effectRun: boolean
		requiredUncCellIds?: number[]
		recommendUncCellIds?: number[][]
		duplicateUncCellIds?: string
		isShowRequiredMore: boolean
		isShowRecommendMore: boolean
		processedRequiredIds: number[]
		groupedRecommendations?: IGroupedRecommendation[]
	}
}

// Кастомный хук для управления накопленными рекомендациями
const useAccumulatedRecommendations = (
	uncCellAssist: IUncCellAssist,
	uncAddBuffer: IUncAddBuffer[],
	isRemovedUnc: boolean
) => {
	const [accumulatedRecommendations, setAccumulatedRecommendations] = useState<IAccumulatedRecommendations>({
		requiredUncCellIds: [],
		recommendUncCellIds: [],
		duplicateUncCellIds: '',
		sourceMap: new Map(),
	})

	const isMountedRef = useRef<boolean>(true)

	useEffect(() => {
		return () => {
			isMountedRef.current = false
		}
	}, [])

	// Эффект для накопления рекомендаций
	useEffect(() => {
		if (!isMountedRef.current || isRemovedUnc) {
			return
		}

		const currentRecommendations = uncCellAssist
			? {
					requiredIds: uncCellAssist.requiredUncCellIds || [],
					recommendIds: uncCellAssist.recommendUncCellIds || [],
					duplicateIds: uncCellAssist.duplicateUncCellIds || '',
			  }
			: {
					requiredIds: [],
					recommendIds: [],
					duplicateIds: '',
			  }

		if (
			!currentRecommendations.requiredIds.length &&
			!currentRecommendations.recommendIds.length &&
			!currentRecommendations.duplicateIds
		) {
			return
		}

		setAccumulatedRecommendations(prev => {
			const newRequiredUncCellIds = [...new Set([...prev.requiredUncCellIds, ...currentRecommendations.requiredIds])]

			const newRecommendUncCellIds = [...prev.recommendUncCellIds]
			const newSourcesMap = new Map(prev.sourceMap)

			if (currentRecommendations.recommendIds.length > 0 && uncAddBuffer.length > 0) {
				const currentUncId = uncAddBuffer[uncAddBuffer.length - 1].uncCell?.id

				currentRecommendations.recommendIds.forEach(recArray => {
					if (!arrayUtils.isArrayInArrayOfArrays(recArray, newRecommendUncCellIds)) {
						newRecommendUncCellIds.push(recArray)

						if (currentUncId) {
							const uncRecs = newSourcesMap.get(currentUncId) || []
							newSourcesMap.set(currentUncId, [...uncRecs, recArray])
						}
					}
				})
			}

			const hasChanges =
				newRequiredUncCellIds.length !== prev.requiredUncCellIds.length ||
				newRecommendUncCellIds.length !== prev.recommendUncCellIds.length ||
				currentRecommendations.duplicateIds !== prev.duplicateUncCellIds

			if (hasChanges) {
				return {
					requiredUncCellIds: newRequiredUncCellIds,
					recommendUncCellIds: newRecommendUncCellIds,
					duplicateUncCellIds: currentRecommendations.duplicateIds || prev.duplicateUncCellIds,
					sourceMap: newSourcesMap,
				}
			}

			return prev
		})
	}, [uncCellAssist, uncAddBuffer, isRemovedUnc])

	// Сброс при пустом буфере
	useEffect(() => {
		if (uncAddBuffer.length === 0 && isMountedRef.current) {
			setAccumulatedRecommendations({
				requiredUncCellIds: [],
				recommendUncCellIds: [],
				duplicateUncCellIds: '',
				sourceMap: new Map(),
			})
		}
	}, [uncAddBuffer.length])

	return accumulatedRecommendations
}

// Кастомный хук для автодобавления расценок
const useAutoAddUncs = (
	accumulatedRecommendations: IAccumulatedRecommendations,
	uncAddBuffer: IUncAddBuffer[],
	dependencyGraph: IDependencyGraph,
	hasCyclicDependency: (startUncId: number, targetUncId: number, visited?: Set<number>) => boolean
) => {
	const dispatch = useDispatch()
	const [autoAddedUncs, setAutoAddedUncs] = useState<Set<number>>(new Set())
	const isAddingRef = useRef<boolean>(false)

	const shouldAutoAddRequired = useMemo(() => {
		return (
			accumulatedRecommendations.requiredUncCellIds &&
			accumulatedRecommendations.requiredUncCellIds.length <= CONSTANTS.MAX_AUTO_ADD_ITEMS &&
			accumulatedRecommendations.requiredUncCellIds.length > 0 &&
			accumulatedRecommendations.requiredUncCellIds.some(id => {
				if (autoAddedUncs.has(id)) return false
				const alreadyInBuffer = uncAddBuffer.some(item => stringUtils.isIdEqual(item.uncCell?.id, id))
				return !alreadyInBuffer
			})
		)
	}, [accumulatedRecommendations.requiredUncCellIds, autoAddedUncs, uncAddBuffer])

	// Эффект автодобавления
	useEffect(() => {
		if (shouldAutoAddRequired && !isAddingRef.current) {
			isAddingRef.current = true

			const uncsToAdd = accumulatedRecommendations.requiredUncCellIds.filter(id => {
				if (autoAddedUncs.has(id)) return false

				const alreadyInBuffer = uncAddBuffer.some(item => stringUtils.isIdEqual(item.uncCell?.id, id))
				if (alreadyInBuffer) return false

				const currentUncId = uncAddBuffer.length > 0 ? Number(uncAddBuffer[uncAddBuffer.length - 1].uncCell.id) : null

				if (currentUncId !== null) {
					const wouldCreateCycle = hasCyclicDependency(id, currentUncId)
					if (wouldCreateCycle) {
						return false
					}
				}

				return true
			})

			if (uncsToAdd.length === 0) {
				isAddingRef.current = false
				return
			}

			let addIndex = 0

			const addNextUnc = () => {
				if (addIndex >= uncsToAdd.length) {
					isAddingRef.current = false
					return
				}

				const id = uncsToAdd[addIndex]
				addIndex++

				dispatch(addUnc(0, id, 1, true))
				setAutoAddedUncs(prev => new Set([...prev, id]))

				const timer = setTimeout(addNextUnc, CONSTANTS.ADD_DELAY)
				return timer
			}

			const firstTimer = addNextUnc()

			return () => {
				if (firstTimer) clearTimeout(firstTimer)
				isAddingRef.current = false
			}
		}
	}, [
		shouldAutoAddRequired,
		dispatch,
		accumulatedRecommendations.requiredUncCellIds,
		autoAddedUncs,
		// uncAddBuffer,
		hasCyclicDependency,
	])

	// Сброс при пустом буфере
	useEffect(() => {
		if (uncAddBuffer.length === 0) {
			setAutoAddedUncs(new Set())
		}
	}, [uncAddBuffer.length])

	return { autoAddedUncs, setAutoAddedUncs, shouldAutoAddRequired }
}

export const UncAddAssist: React.FC<Props> = ({ uncCellAssist, uncAddBuffer }) => {
	const { uncCells } = useSelector((state: IState) => state.userProjects)
	const { uncMains } = useSelector((state: IState) => state.public)
	const { enableUncAssist } = useSelector((state: IState) => state.userProjects)
	const dispatch = useDispatch()

	// Состояния
	const [lastActiveUnc, setLastActiveUnc] = useState<{
		uncCellAssist: typeof uncCellAssist
		uncCell: any
	} | null>(null)
	const [recentlyAddedUnc, setRecentlyAddedUnc] = useState<number | null>(null)

	// Деструктуризация с значениями по умолчанию
	const { requiredUncCellIds = [], recommendUncCellIds = [] } = uncCellAssist || {}

	// Ключ для кеша - берем id из первой расценки в буфере
	const cacheKey = useMemo(() => {
		if (uncAddBuffer && uncAddBuffer.length > 0 && uncAddBuffer[0].uncCell) {
			return `${uncAddBuffer[0].uncCell.id}`
		}
		return 'default'
	}, [uncAddBuffer])

	// Состояния UI
	const [isShowRequiredMore, setIsShowRequiredMore] = useState(false)
	const [isShowRecommendMore, setIsShowRecommendMore] = useState(false)

	// Состояние для отслеживания развернутости групп
	const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>({})

	// Ref для отслеживания, смонтирован ли компонент
	const isMountedRef = useRef<boolean>(true)

	// Отслеживание предыдущей длины буфера для определения удаления расценки
	const [prevUncAddBufferLength, setPrevUncAddBufferLength] = useState<number>(uncAddBuffer.length)

	// Состояние для отслеживания удаления расценки
	const [isRemovedUnc, setIsRemovedUnc] = useState<boolean>(false)

	// Кеш данных для разных расценок
	const assistCacheRef = useRef<UncAssistCache>({})

	// Граф зависимостей для отслеживания циклических зависимостей
	const [dependencyGraph, setDependencyGraph] = useState<IDependencyGraph>({})

	// Используем кастомные хуки
	const accumulatedRecommendations = useAccumulatedRecommendations(uncCellAssist, uncAddBuffer, isRemovedUnc)

	// Функция для проверки циклических зависимостей
	const hasCyclicDependency = useCallback(
		(startUncId: number, targetUncId: number, visited: Set<number> = new Set()): boolean => {
			if (visited.has(startUncId)) {
				return false
			}

			visited.add(startUncId)

			if (dependencyGraph[startUncId]?.includes(targetUncId)) {
				return true
			}

			return (dependencyGraph[startUncId] || []).some(dependentId =>
				hasCyclicDependency(dependentId, targetUncId, new Set(visited))
			)
		},
		[dependencyGraph]
	)

	const { autoAddedUncs, setAutoAddedUncs, shouldAutoAddRequired } = useAutoAddUncs(
		accumulatedRecommendations,
		uncAddBuffer,
		dependencyGraph,
		hasCyclicDependency
	)

	// Эффект для отслеживания удаления расценки
	useEffect(() => {
		if (uncAddBuffer.length < prevUncAddBufferLength) {
			setIsRemovedUnc(true)

			if (uncAddBuffer.length === 0 && isMountedRef.current) {
				setIsShowRequiredMore(false)
				setIsShowRecommendMore(false)
			}
		} else {
			setIsRemovedUnc(false)
		}

		setPrevUncAddBufferLength(uncAddBuffer.length)
	}, [uncAddBuffer.length, prevUncAddBufferLength])

	// Эффект для отслеживания изменений в активной расценке
	useEffect(() => {
		if (uncCellAssist || uncAddBuffer[uncAddBuffer.length - 1]?.uncCell) {
			setLastActiveUnc({
				uncCellAssist,
				uncCell: uncAddBuffer[uncAddBuffer.length - 1]?.uncCell,
			})
		}
	}, [uncCellAssist, uncAddBuffer])

	// Определяем, нужно ли показывать компонент
	const shouldShowAssistant = useMemo(() => {
		return uncAddBuffer.length > 0 && enableUncAssist
	}, [uncAddBuffer, enableUncAssist])

	// Обработчик переключения развернутости группы
	const toggleGroup = (groupIndex: number) => {
		setExpandedGroups(prev => ({
			...prev,
			[groupIndex]: !prev[groupIndex],
		}))
	}

	// Определяем, нужно ли показывать блок рекомендаций
	const shouldShowRecommendations = useMemo(() => {
		return (
			Array.isArray(accumulatedRecommendations.recommendUncCellIds) &&
			accumulatedRecommendations.recommendUncCellIds.length > 0
		)
	}, [accumulatedRecommendations.recommendUncCellIds])

	// Создаем запись в кеше, если её еще нет
	useEffect(() => {
		if (!isMountedRef.current) return

		if (!assistCacheRef.current[cacheKey]) {
			assistCacheRef.current[cacheKey] = {
				effectRun: false,
				requiredUncCellIds: accumulatedRecommendations.requiredUncCellIds,
				recommendUncCellIds: accumulatedRecommendations.recommendUncCellIds,
				duplicateUncCellIds: accumulatedRecommendations.duplicateUncCellIds,
				isShowRequiredMore: false,
				isShowRecommendMore: shouldShowRecommendations,
				processedRequiredIds: [],
				groupedRecommendations: [],
			}
		}
	}, [cacheKey, accumulatedRecommendations, shouldShowRecommendations])

	// Обновление данных в кеше при изменении accumulated рекомендаций
	useEffect(() => {
		if (!assistCacheRef.current[cacheKey]) return

		const cachedRequiredIds = assistCacheRef.current[cacheKey].requiredUncCellIds || []
		const newRequiredIds = accumulatedRecommendations.requiredUncCellIds

		const hasNewRequiredIds =
			Array.isArray(newRequiredIds) && newRequiredIds.some(id => !cachedRequiredIds.includes(id))

		assistCacheRef.current[cacheKey].requiredUncCellIds = newRequiredIds
		assistCacheRef.current[cacheKey].recommendUncCellIds = accumulatedRecommendations.recommendUncCellIds
		assistCacheRef.current[cacheKey].duplicateUncCellIds = accumulatedRecommendations.duplicateUncCellIds

		if (hasNewRequiredIds || cachedRequiredIds.length !== newRequiredIds.length) {
			assistCacheRef.current[cacheKey].effectRun = false

			if (newRequiredIds.length > CONSTANTS.MAX_AUTO_ADD_ITEMS) {
				setIsShowRequiredMore(true)
				assistCacheRef.current[cacheKey].isShowRequiredMore = true
			}
		}
	}, [accumulatedRecommendations, cacheKey])

	// Эффект для обновления графа зависимостей при изменении данных ассистента
	useEffect(() => {
		if (!uncCellAssist || !uncCellAssist.requiredUncCellIds || uncCellAssist.requiredUncCellIds.length === 0) {
			return
		}

		if (uncAddBuffer.length > 0 && uncAddBuffer[uncAddBuffer.length - 1]?.uncCell) {
			const currentUncId = Number(uncAddBuffer[uncAddBuffer.length - 1].uncCell.id)
			const newDependencies = uncCellAssist.requiredUncCellIds.map(Number)

			setDependencyGraph(prev => {
				const currentDependencies = prev[currentUncId]
				const hasChanges =
					!currentDependencies ||
					currentDependencies.length !== newDependencies.length ||
					!currentDependencies.every((dep, index) => dep === newDependencies[index])

				if (hasChanges) {
					return {
						...prev,
						[currentUncId]: newDependencies,
					}
				}
				return prev
			})
		}
	}, [uncCellAssist, uncAddBuffer])

	const getFullUnc = useCallback(
		(uncID: number | string | null): IUnc | null => {
			if (!uncID) return null
			const uncCell = uncCells.find(unc => unc.id === uncID)
			if (uncCell?.code) return { ...uncCell, id: Number(uncCell.id) }

			const uncMain = uncMains.find(unc => uncID === unc.id)
			return uncMain ? { code: `${uncMain.code}. ${uncMain.name}`, id: uncMain.id } : null
		},
		[uncCells, uncMains]
	)

	const getFullTableName = useCallback(
		(uncId: number | string): IUnc | null => {
			const uncCell = uncCells.find(unc => unc.id === uncId)
			const uncMain = uncMains.find(unc => uncCell?.uncMainId === unc.id)

			if (!uncMain) return null

			const codeParts = uncMain.code.split('.')
			const dotIndex = uncMain.code.indexOf('.')
			const afterDotChar = uncMain.code[dotIndex + 1]

			let formattedCode = ''

			if (afterDotChar && afterDotChar !== ' ') {
				// пример: "12.B" → включаем "12.B"
				formattedCode = uncMain.code
			} else {
				// пример: "12. A" → берём "12"
				formattedCode = codeParts[0]
			}

			const code = `${formattedCode}${uncMain.name ? ` - ${uncMain.name}` : ''}`

			return { code, id: uncMain.id }
		},
		[uncCells, uncMains]
	)

	// Группируем рекомендации по источнику
	const groupedRecommendations = useMemo(() => {
		if (
			!Array.isArray(accumulatedRecommendations.recommendUncCellIds) ||
			accumulatedRecommendations.recommendUncCellIds.length === 0
		) {
			return []
		}

		const groups: Record<string, IGroupedRecommendation> = {}

		accumulatedRecommendations.recommendUncCellIds.forEach((recArray: number[]) => {
			if (!Array.isArray(recArray) || recArray.length === 0) return

			const sourceId = recArray[0]
			const sourceUnc = getFullUnc(sourceId)
			const sourceKey = String(sourceId)

			if (!groups[sourceKey]) {
				groups[sourceKey] = {
					sourceUncId: Number(sourceId),
					sourceName: sourceUnc ? sourceUnc.code : `Расценка ${sourceId}`,
					recommendations: [],
				}
			}

			groups[sourceKey].recommendations.push(recArray)
		})

		return Object.values(groups)
	}, [accumulatedRecommendations.recommendUncCellIds, getFullUnc])

	// NEW: Группируем обязательные расценки по источникам из буфера
	const groupedRequiredRecommendations = useMemo(() => {
		if (!accumulatedRecommendations.requiredUncCellIds?.length || !uncAddBuffer?.length) return []

		const sourceMap = new Map<string, { source: IUnc; requiredIds: number[] }>()

		accumulatedRecommendations.requiredUncCellIds.forEach(requiredId => {
			const sourceUnc = uncAddBuffer[0]?.uncCell || null

			if (sourceUnc) {
				const sourceKey = String(sourceUnc.id)

				if (!sourceMap.has(sourceKey)) {
					sourceMap.set(sourceKey, {
						source: sourceUnc,
						requiredIds: [],
					})
				}

				const group = sourceMap.get(sourceKey)
				if (group && !group.requiredIds.includes(requiredId)) {
					group.requiredIds.push(requiredId)
				}
			}
		})

		return Array.from(sourceMap.values())
	}, [accumulatedRecommendations.requiredUncCellIds, uncAddBuffer])

	// Расширенная версия correctRecommendUncs для каждой группы
	const processGroupedRecommendations = useMemo(() => {
		return groupedRecommendations.map(group => {
			const processedRecs = group.recommendations.map(unc => {
				const uniqueOptions = [...new Set(unc)]
				const tableName = getFullTableName(uniqueOptions[0])
				return {
					options: [tableName?.id, ...uniqueOptions].filter(Boolean) as number[],
					sourceId: group.sourceUncId,
					sourceName: group.sourceName,
					tableNameObj: tableName,
				}
			})

			return {
				...group,
				processedRecommendations: processedRecs,
			}
		})
	}, [groupedRecommendations, getFullTableName])

	// NEW: Обработка группированных обязательных расценок для селектов
	const processGroupedRequiredRecommendations = useMemo(() => {
		if (!groupedRequiredRecommendations.length) return []

		return groupedRequiredRecommendations.map(group => {
			// Группируем обязательные расценки по таблицам
			const tableGroups = new Map<string, { tableNameObj: IUnc | null; requiredIds: number[] }>()

			group.requiredIds.forEach(requiredId => {
				const tableName = getFullTableName(requiredId)
				const tableKey = tableName?.id ? String(tableName.id) : 'no-table'

				if (!tableGroups.has(tableKey)) {
					tableGroups.set(tableKey, {
						tableNameObj: tableName,
						requiredIds: [],
					})
				}

				const tableGroup = tableGroups.get(tableKey)
				if (tableGroup) {
					tableGroup.requiredIds.push(requiredId)
				}
			})

			return {
				...group,
				processedRequired: Array.from(tableGroups.values()).map(tableGroup => ({
					options: tableGroup.requiredIds,
					tableNameObj: tableGroup.tableNameObj,
				})),
			}
		})
	}, [groupedRequiredRecommendations, getFullTableName])

	// Инициализация состояния групп при изменении их количества
	useEffect(() => {
		const initialState = processGroupedRequiredRecommendations.reduce((acc, _, index) => {
			acc[index] = true
			return acc
		}, {} as Record<number, boolean>)
		setExpandedGroups(initialState)
	}, [processGroupedRequiredRecommendations.length])

	const correctRecommendUncs = useMemo(() => {
		if (!Array.isArray(accumulatedRecommendations.recommendUncCellIds)) return []

		return accumulatedRecommendations.recommendUncCellIds.map(unc => {
			const uniqueOptions = [...new Set(unc)]
			const tableName = getFullTableName(uniqueOptions[0])
			return [tableName?.id, ...uniqueOptions].filter(Boolean)
		})
	}, [accumulatedRecommendations.recommendUncCellIds, getFullTableName])

	// Модифицируем handleAddUnc для проверки циклических зависимостей
	const handleAddUnc = (event: React.ChangeEvent<{ value: unknown }>) => {
		const selectedValue = event.target.value as number
		if (selectedValue) {
			if (autoAddedUncs.has(selectedValue)) {
				return
			}

			const alreadyInBuffer = uncAddBuffer.some(item => stringUtils.isIdEqual(item.uncCell?.id, selectedValue))
			if (alreadyInBuffer) {
				return
			}

			const currentUncId = uncAddBuffer.length > 0 ? Number(uncAddBuffer[uncAddBuffer.length - 1].uncCell.id) : null

			if (currentUncId !== null) {
				const wouldCreateCycle = hasCyclicDependency(selectedValue, currentUncId)
				if (wouldCreateCycle) {
					return
				}
			}

			dispatch(addUnc(0, selectedValue, 1, true))
			setRecentlyAddedUnc(selectedValue)
			setAutoAddedUncs(prev => new Set([...prev, selectedValue]))

			if (accumulatedRecommendations.requiredUncCellIds.length === 1) {
				setIsShowRequiredMore(false)
			}
		}
	}

	// Форматируем текст для отображения расценки
	const formatUncText = (unc: IUnc | null) => {
		if (!unc?.code) return CONSTANTS.MESSAGES.NO_DATA

		// Для таблицы выделяем жирным
		if (unc?.name?.includes('.')) {
			return `${unc.code} - ${unc.name}${unc.parameter ? ` ${unc.parameter}` : ''}`
		}

		// Обычная расценка
		const codePrefix = unc.code.split('.')[0]
		const codeSuffix = unc.code.split('.')[1] || ''

		return unc.name
			? `${codePrefix}.${codeSuffix} - ${unc.name}${unc.parameter ? ` ${unc.parameter}` : ''}`
			: unc.parameter
			? `${unc.code} ${unc.parameter}`
			: unc.code
	}

	// Форматируем текст для отображения таблицы расценок в селекте
	const formatTableText = (unc: IUnc | null) => {
		if (!unc?.code) return CONSTANTS.MESSAGES.SELECT_UNC
		// Извлекаем код таблицы (обычно первая часть до первой точки)
		const tableCode = unc.code.split('.')[0]
		const tableName = unc.code.split('. ')[1] || ''

		return `${tableCode}${tableName ? ` - ${tableName}` : ''}`
	}

	// Здесь проверяем условие отображения вместо раннего возврата
	return shouldShowAssistant ? (
		<div className='assist-main'>
			{/* Блок обязательных расценок с группировкой по родительским расценкам - показываем только если больше 3 */}
			{accumulatedRecommendations.requiredUncCellIds?.length > CONSTANTS.MAX_AUTO_ADD_ITEMS && (
				<div className='assist-block'>
					<div className='assist-block_header' onClick={() => setIsShowRequiredMore(!isShowRequiredMore)}>
						<div>Рекомендуемые к обязательному добавлению расценки</div>
						<div>
							<svg
								width='10'
								height='6'
								viewBox='0 0 10 6'
								fill='none'
								xmlns='http://www.w3.org/2000/svg'
								className={isShowRequiredMore ? 'svg-rotate turn' : 'svg-rotate'}
							>
								<path
									d='M9 1L5 5L1 1'
									stroke='#3F4F5E'
									strokeWidth='1.6'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
							</svg>
						</div>
					</div>
					{isShowRequiredMore && (
						<div className='assist-block_uncs'>
							{processGroupedRequiredRecommendations.map((group, groupIndex) => (
								<div key={`required-group-${groupIndex}`} className='required-group'>
									<div
										className='required-group-header'
										onClick={() => toggleGroup(groupIndex)}
										style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
									>
										<Typography variant='subtitle2' className='required-group-title'>
											Для расценки: {formatUncText(group.source)}
										</Typography>
										<svg
											width='16'
											height='10'
											viewBox='0 0 16 10'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
											className={expandedGroups[groupIndex] ? 'svg-rotate turn' : 'svg-rotate'}
											style={{ marginLeft: '10px', transition: 'transform 0.2s ease' }}
										>
											<path
												d='M14 2L8 8L2 2'
												stroke='#d32f2f'
												strokeWidth='2.5'
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
										</svg>
									</div>

									{expandedGroups[groupIndex] &&
										group.processedRequired.map((item, recIndex) => {
											return (
												<div key={`required-group-${groupIndex}-rec-${recIndex}`} className='recommendation-item'>
													<Tooltip
														title={
															item.tableNameObj?.code
																? `Таблица расценок: ${item.tableNameObj.code}`
																: CONSTANTS.MESSAGES.NO_DATA
														}
														enterDelay={CONSTANTS.TOOLTIP.ENTER_DELAY}
														enterNextDelay={CONSTANTS.TOOLTIP.NEXT_DELAY}
														placement={CONSTANTS.TOOLTIP.PLACEMENT}
													>
														<Select
															labelId={`select-required-label-${groupIndex}-${recIndex}`}
															label={CONSTANTS.MESSAGES.SELECT_UNC}
															placeholder={CONSTANTS.MESSAGES.SELECT_UNC}
															value=''
															displayEmpty
															renderValue={() => item.tableNameObj?.code}
															className='select-input required'
															onChange={handleAddUnc}
															key={`select-required-${recentlyAddedUnc}-${groupIndex}-${recIndex}`}
														>
															{Array.isArray(item.options) &&
																item.options.map((uncId: number) => {
																	const unc = getFullUnc(uncId)
																	if (!unc || !unc.code) return null

																	return (
																		<MenuItem
																			key={unc.id || `required-unc-${uncId}`}
																			value={unc.id}
																			className='required'
																		>
																			{unc ? formatUncText(unc) : CONSTANTS.MESSAGES.NO_DATA}
																		</MenuItem>
																	)
																})}
														</Select>
													</Tooltip>
												</div>
											)
										})}
								</div>
							))}
						</div>
					)}
				</div>
			)}

			{/* Блок возможных расценок к добавлению - обновленный с группировкой */}
			<div className='assist-block'>
				<div className='assist-block_header'>
					<div>Возможные к добавлению расценки по решению пользователя</div>
				</div>
				<div className='assist-block_uncs'>
					{processGroupedRecommendations.length ? (
						<>
							{processGroupedRecommendations.map((group, groupIndex) => (
								<div key={`group-${group.sourceUncId}`} className='recommendation-group'>
									{group.processedRecommendations.map((rec, recIndex) => {
										return (
											<div key={`group-${groupIndex}-rec-${recIndex}`} className='recommendation-item'>
												<Tooltip
													title={
														rec.tableNameObj?.code
															? `Таблица расценок: ${rec.tableNameObj.code}`
															: CONSTANTS.MESSAGES.NO_DATA
													}
													enterDelay={CONSTANTS.TOOLTIP.ENTER_DELAY}
													enterNextDelay={CONSTANTS.TOOLTIP.NEXT_DELAY}
													placement={CONSTANTS.TOOLTIP.PLACEMENT}
												>
													<Select
														labelId={`select-label-${groupIndex}-${recIndex}`}
														label={CONSTANTS.MESSAGES.SELECT_UNC}
														placeholder={CONSTANTS.MESSAGES.SELECT_UNC}
														value=''
														displayEmpty
														renderValue={() => rec.tableNameObj?.code}
														className='select-input possible'
														onChange={handleAddUnc}
														key={`select-${recentlyAddedUnc}-${groupIndex}-${recIndex}`}
													>
														{Array.isArray(rec.options) &&
															rec.options.map((id: any) => {
																if (!id) return null

																const unc = getFullUnc(id)
																if (!unc || !unc.code) return null

																// Это таблица (первый элемент), пропускаем его
																if (rec.tableNameObj && unc.id === rec.tableNameObj.id) {
																	return null
																}

																return (
																	<MenuItem
																		key={unc.id || `unc-${id}`}
																		value={unc.id}
																		className={`possible ${
																			Array.isArray(correctRecommendUncs) &&
																			correctRecommendUncs.some((itm: any[]) => unc.id === itm?.[0])
																				? 'dissabled'
																				: ''
																		}`}
																	>
																		{unc ? formatUncText(unc) : CONSTANTS.MESSAGES.NO_DATA}
																	</MenuItem>
																)
															})}
													</Select>
												</Tooltip>
											</div>
										)
									})}
								</div>
							))}
						</>
					) : (
						<div style={{ padding: '8px' }}>{CONSTANTS.MESSAGES.NO_RECOMMENDED}</div>
					)}
				</div>
			</div>

			{/* Блок дублирования расценок */}
			<div className='assist-block'>
				<div className='assist-block-title'>Дублирование расценок</div>
				{accumulatedRecommendations.duplicateUncCellIds ? (
					<div className='assist-block-subtitle'>
						{accumulatedRecommendations.duplicateUncCellIds.split('\n').map((str: string, i: number) => {
							return <div key={i}>{str}</div>
						})}
					</div>
				) : (
					<div style={{ padding: '8px' }}>{CONSTANTS.MESSAGES.NO_DUPLICATES}</div>
				)}
			</div>
		</div>
	) : null
}

export default UncAddAssist
